import { logger, normalizeRouterQuery } from '@automata/utils';
import { useSaveWorkflow } from '@automata/wfb-api';
import set from 'lodash/set';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useWorkcellForm } from './useWorkcellForm';
import { setWorkflowIsSaved } from '../../../features/ui/uiSlice';
import {
  selectDriversByInstrumentId,
  selectWorkcellTransportConfigId,
  selectWorkcellTransportConfigType,
  selectWorkcellTransportConfigVersion,
} from '../../../features/workflow/selectors';
import {
  setWorkcellTransportConfig,
  setWorkcellTransportConfigType,
  setWorkcellTransportConfigVersion,
  setWorkflowInstruments,
  setWorkflowMetadata,
} from '../../../features/workflow/workflowSlice';
import { selectWorkflowJsonToSave } from '../../../features/workflowJsonSelectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { buildConfigSchema } from '../schemas/workcellConfigSchema';
import { WorkcellConfig } from '../types';

type FullInstrument = {
  bench: string;
  driverName: string;
  driverVersion: string;
  capacity: number;
  config: Record<string, any>;
  dataConnectorConfig?: {
    inputActions?: string[];
    outputActions?: string[];
  };
  is_slotless_container?: boolean;
  mappings?: string[];
  simulate?: boolean;
};

type FullWorkcellConfig = Record<string, FullInstrument>;

export function useWorkcellFormWrapper({
  initialConfig,
  defaultWorkcellConfig,
  defaultTransportConfigId,
  onClose,
}: {
  initialConfig: WorkcellConfig;
  defaultWorkcellConfig: WorkcellConfig;
  defaultTransportConfigId?: string;
  onClose: () => void;
}) {
  const dispatch = useAppDispatch();
  const driversByInstrumentId = useAppSelector(selectDriversByInstrumentId);
  const savedTransportConfigId = useAppSelector(
    selectWorkcellTransportConfigId
  );
  const savedTransportConfigType = useAppSelector(
    selectWorkcellTransportConfigType
  );
  const savedTransportConfigVersion = useAppSelector(
    selectWorkcellTransportConfigVersion
  );

  const workflowToSave = useAppSelector(selectWorkflowJsonToSave);
  const { query } = useRouter();
  const normalizedQuery = normalizeRouterQuery(query);
  const workflowId = normalizedQuery.id;

  const {
    canSave,
    errors,
    formValues,
    preparedInstruments,
    handleSave,
    isFieldChanged,
    isInstrumentChanged,
    isFormChanged: isInstrumentFormChanged,
    resetField,
    resetForm,
    resetToDefaultWorkcell,
    validateAndUpdate,
    updateSimulateField,
    isSimulateFieldChanged,
  } = useWorkcellForm(
    initialConfig,
    driversByInstrumentId,
    onClose,
    defaultWorkcellConfig
  );

  const router = useRouter();

  const [transportConfigId, setTransportConfigId] = useState<
    string | undefined
  >(savedTransportConfigId);
  const [transportConfigType, setTransportConfigTypeLocal] = useState<
    'V1' | 'V3' | undefined
  >(savedTransportConfigType);
  const [transportConfigVersion, setTransportConfigVersionLocal] = useState<
    number | undefined
  >(savedTransportConfigVersion);

  useEffect(() => {
    setTransportConfigId(savedTransportConfigId);
    setTransportConfigTypeLocal(savedTransportConfigType);
    setTransportConfigVersionLocal(savedTransportConfigVersion);
  }, [
    savedTransportConfigId,
    savedTransportConfigType,
    savedTransportConfigVersion,
  ]);

  const { saveWorkflow } = useSaveWorkflow({
    onSuccess: (data) => {
      handleSave();
      if (workflowId === 'new') {
        router.replace({
          query: { ...router.query, initialSave: true, id: data.id },
        });
      }
      dispatch(setWorkflowMetadata(data));
    },
    onError: (error) => {
      logger.error('Failed to save workcell from modal', error);
    },
  });

  const isTransportConfigChanged = useMemo(() => {
    return (
      transportConfigId !== savedTransportConfigId ||
      transportConfigType !== savedTransportConfigType ||
      transportConfigVersion !== savedTransportConfigVersion
    );
  }, [
    savedTransportConfigId,
    savedTransportConfigType,
    savedTransportConfigVersion,
    transportConfigId,
    transportConfigType,
    transportConfigVersion,
  ]);

  const isFormChanged = useCallback(
    (valuesToCompare: WorkcellConfig) => {
      return (
        isInstrumentFormChanged(valuesToCompare) || isTransportConfigChanged
      );
    },
    [isInstrumentFormChanged, isTransportConfigChanged]
  );

  const handleTransportConfigChange = useCallback(
    ({
      id,
      type,
      version,
    }: {
      id?: string;
      type?: 'V1' | 'V3';
      version?: number;
    }) => {
      setTransportConfigId(id);
      setTransportConfigTypeLocal(type);
      setTransportConfigVersionLocal(version);
    },
    []
  );

  const handleCancel = () => {
    resetForm();
    setTransportConfigId(savedTransportConfigId);
    setTransportConfigTypeLocal(savedTransportConfigType);
    setTransportConfigVersionLocal(savedTransportConfigVersion);
    onClose();
  };

  const handleUpdateClick = () => {
    try {
      for (const [name, instrument] of Object.entries(formValues)) {
        const schema = driversByInstrumentId[name]?.configuration as any;
        if (!schema) {
          logger.error(`Missing driver configuration for instrument ${name}`);
          return;
        }
        buildConfigSchema(schema).parse(instrument.config);
      }

      const updated = Object.fromEntries(
        Object.entries(formValues).map(([key, value]) => {
          // As a basis we should take the actual configuration of workcells.
          // This will help with working with outdated workflows.
          const base = defaultWorkcellConfig[key] as unknown as FullInstrument;
          if (!base) {
            throw new Error(`Missing base config for ${key}`);
          }

          return [
            key,
            {
              ...base,
              simulate: value.simulate,
              config: value.config,
            },
          ];
        })
      ) as FullWorkcellConfig;

      dispatch(setWorkflowInstruments(updated));
      dispatch(setWorkcellTransportConfig(transportConfigId));
      dispatch(setWorkcellTransportConfigType(transportConfigType));
      dispatch(setWorkcellTransportConfigVersion(transportConfigVersion));

      let workflowToSaveWithUpdatedWorkcell = set(
        { ...workflowToSave },
        'workflow.layout.data.workcellInstruments',
        updated
      );
      workflowToSaveWithUpdatedWorkcell = set(
        workflowToSaveWithUpdatedWorkcell,
        'workflow.layout.data.workcellTransportConfigId',
        transportConfigId
      );
      workflowToSaveWithUpdatedWorkcell = set(
        workflowToSaveWithUpdatedWorkcell,
        'workflow.layout.data.workcellTransportConfigType',
        transportConfigType
      );
      workflowToSaveWithUpdatedWorkcell = set(
        workflowToSaveWithUpdatedWorkcell,
        'workflow.layout.data.workcellTransportConfigVersion',
        transportConfigVersion
      );
      workflowToSaveWithUpdatedWorkcell = set(
        workflowToSaveWithUpdatedWorkcell,
        'workcell.workcell_id',
        transportConfigId && transportConfigType ? null : transportConfigId
      );
      workflowToSaveWithUpdatedWorkcell = set(
        workflowToSaveWithUpdatedWorkcell,
        'workcell.transport_config',
        transportConfigId && transportConfigType
          ? {
              id: transportConfigId,
              type: transportConfigType,
              ...(transportConfigVersion !== undefined
                ? { version: transportConfigVersion }
                : {}),
            }
          : undefined
      );

      if (workflowId === 'new') {
        saveWorkflow(workflowToSaveWithUpdatedWorkcell);
      } else {
        saveWorkflow(workflowToSaveWithUpdatedWorkcell, workflowId);
      }

      dispatch(setWorkflowIsSaved(true));
    } catch (error) {
      logger.error('Validation failed', error);
    }
  };

  return {
    canSave,
    formValues,
    preparedInstruments,
    handleCancel,
    handleTransportConfigChange,
    handleUpdateClick,
    isTransportConfigChanged,
    resetToDefaultWorkcell: () => {
      resetToDefaultWorkcell();
      setTransportConfigId(defaultTransportConfigId);
      setTransportConfigTypeLocal(undefined);
      setTransportConfigVersionLocal(undefined);
    },
    isFieldChanged,
    isInstrumentChanged,
    isFormChanged,
    resetField,
    transportConfigId,
    transportConfigType,
    transportConfigVersion,
    validateAndUpdate,
    updateSimulateField,
    isSimulateFieldChanged,
    errors,
  };
}
